<template>
	<div class="filter-bar-container">
		<FilterBarProviderMulti
			v-if="selectionType === 'bundle' && selectedProviders.length > 0"
			:providers="selectedProviders"
			:currentFilters="currentFilters"
			:updateRouteState="updateRouteState"
			:userSelectedPackages="userSelectedPackages ?? []"
		/>
		<HiddenHorizontalScrollbar class="filter-bar-seo__provider-row">
			<div
				v-if="plusIconIsVisible && showAddButtonFirst"
				class="filter-bar-seo__provider-icon filter-bar-seo__provider-icon--plus"
			>
				<button @click.prevent="!isModalOpening && openProviderSelection()">
					<img :src="plusIcon" alt="Provider Selection" />
				</button>
			</div>
			<div
				v-for="(provider, index) in filteredProviders"
				:key="provider.packageId"
				:id="`filterbar-${provider.packageId}`"
				class="filter-bar-seo__provider-icon filter-bar-seo__provider-icon--provider"
				:class="{
					active: isProviderActive(provider.shortName),
					disabled: isProviderDisabled(provider.packageId),
				}"
			>
				<component
					:is="providerTag"
					:href="!isProviderDisabled(provider.packageId) ? providerUrls[provider.shortName] : null"
					@click.prevent="providerClicked(provider.shortName)"
				>
					<ProviderIconSquare
						:provider="provider"
						:loading="index >= imgProvidersBarLoadCount ? 'lazy' : 'eager'"
					/>
				</component>
			</div>

			<div
				v-if="plusIconIsVisible && !showAddButtonFirst"
				class="filter-bar-seo__provider-icon filter-bar-seo__provider-icon--plus"
			>
				<button @click.prevent="!isModalOpening && openProviderSelection()">
					<img :src="plusIcon" alt="Provider Selection" />
				</button>
			</div>
		</HiddenHorizontalScrollbar>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, defineAsyncComponent } from 'vue';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { calculateUrl } from '@/helpers/state-helper';

import { NewPageType } from '@/@types/graphql-types';
import {
	GetUpcomingProvidersDocument,
	GetUpcomingProvidersQueryVariables,
	GetUpcomingProvidersQuery,
} from '@/pages/graphql/queries/GetUpcomingProviders.query';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';

import { PackagesSelectionModal } from '@/lazy-load-components';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderIconSquare from '@/features/ui/components/ProviderIconSquare.vue';
import { useRouter } from 'nuxt/app';
import { WebLocale } from '@/enums/web-locale';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import Provider from '@/interfaces/provider';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

const FilterBarProviderMulti = defineAsyncComponent(
	() => import('@/components/filter-bar/seo/FilterBarProviderMulti.vue')
);

interface FilterBarProviderSeoProps {
	collectionType: CollectionType;
	currentFilters: FilterCollection;
	webLocale: WebLocale;
	addButtonVisible?: boolean;
	showAddButtonFirst?: boolean;
	userProviders: Provider[];
	newPageType?: NewPageType;
	providersClickable?: boolean;
	updateRouteState?: boolean;
	trackEvent?: boolean;
	userSelectedPackages?: string[];
	selectionType?: 'single' | 'bundle';
}

const props = withDefaults(defineProps<FilterBarProviderSeoProps>(), {
	addButtonVisible: true,
	showAddButtonFirst: false,
	providersClickable: true,
	updateRouteState: true,
	trackEvent: true,
	selectionType: 'single',
});

const emit = defineEmits<{
	(
		event: 'toggle',
		payload: {
			filterName: keyof FilterCollection;
			value: any;
			selectedState: 1 | 0;
			updateRouteState?: boolean;
			userSelectedPackages?: string[];
			trackEvent?: boolean;
		}
	): void;
}>();

const router = useRouter();

const constantStore = useConstantStore();
const filterStore = useFilterStore();
const languageStore = useLanguageStore();
const routingStore = useRoutingStore();
const userStore = useUserStore();

const { imgProvidersBarLoadCount } = useRuntimeConfig().public;
const plusIcon = `/${ASSETS_DIR}/img/plus_icon.png`;

const isModalOpening = ref(false);
const providerUrls = ref<Record<string, string>>({});

const enabled = computed(() =>
	['app.titles.upcoming', 'app.titles.new', 'app.titles.pricedrops'].some(routeName =>
		routingStore.activeRoute?.name?.startsWith(routeName)
	)
);

const { result, onResult } = useQuery<GetUpcomingProvidersQuery, GetUpcomingProvidersQueryVariables>(
	GetUpcomingProvidersDocument,
	() => ({
		country: languageStore.country,
		language: languageStore.language,
		pageType: NewPageType.Upcoming,
		first: 32,
	}),
	{ enabled: enabled }
);

const upcomingProvidersData = computed(() => {
	const upcomingReleases =
		result.value?.newTitles?.edges
			?.map(title => {
				return title?.node?.content?.upcomingReleases;
			})
			?.flat() ?? [];

	const providerIds = upcomingReleases.map(provider => provider?.package?.packageId);

	// Return array without duplicates or undefined values
	return [...new Set(providerIds)].filter(Boolean) as number[];
});

onResult(() => {
	constantStore.setUpcomingProviders(upcomingProvidersData.value);
});

const selectedProviders = computed(() =>
	props.userProviders.filter(provider => props.currentFilters.providers.includes(provider.shortName))
);

const plusIconIsVisible = computed(() => {
	if (props.addButtonVisible) {
		return userStore.settings?.providers?.length > 0;
	}
	return props.addButtonVisible;
});

const filteredProviders = computed(() => {
	if (props.selectionType === 'bundle') {
		return props.userProviders.filter(provider => !props.currentFilters.providers.includes(provider.shortName));
	}
	return props.userProviders;
});

const isPricedropsPage = computed(() => {
	return (
		props.collectionType === CollectionType.NEW &&
		routingStore.activeRoute?.meta?.newPageType === NewPageType.PriceDrop
	);
});

const recalculatedProviderUrlProp = computed(() => {
	return `${props.webLocale}_${isPricedropsPage.value ? 'pricedrop' : 'normal'}`;
});

const providerTag = computed(() => (isPricedropsPage.value ? 'button' : 'a'));

watch(
	recalculatedProviderUrlProp,
	async () => {
		const providersByShortNameVal = constantStore.providersByShortName;
		const genresByShortNameVal = constantStore.genresByShortName;
		const subgenresListVal = constantStore.subgenresList;

		if (isPricedropsPage.value) {
			providerUrls.value = {};
		} else {
			providerUrls.value = props.userProviders.reduce((root, provider: Provider) => {
				root[provider.shortName] = calculateUrl(
					router,
					undefined,
					props.collectionType,
					{
						// we only want content_types and providers in the <a href>.
						// no ?genres=crm or other filters.
						content_types: props.currentFilters.content_types,
						providers: [provider.shortName],
					},
					providersByShortNameVal,
					genresByShortNameVal,
					subgenresListVal
				);
				return root;
			}, {} as Record<string, string>);
		}
	},
	{ immediate: true }
);

const isFiltersContainsProvider = (providerName: string) => {
	return props.currentFilters.providers.includes(providerName);
};

const isProviderActive = (providerName: string) => {
	return props.currentFilters.providers.length === 0 || isFiltersContainsProvider(providerName);
};

const isProviderDisabled = (providerId: number) => {
	return props.collectionType === CollectionType.UPCOMING && !upcomingProvidersData.value.includes(providerId);
};

async function providerClicked(providerName: string) {
	if (!props.providersClickable) return;

	const isActive: 1 | 0 = isFiltersContainsProvider(providerName) ? 0 : 1;

	const toggleData = {
		filterName: 'providers',
		value: providerName,
		selectedState: isActive,
		updateRouteState: props.updateRouteState,
		userSelectedPackages: props.userSelectedPackages,
		trackEvent: props.trackEvent,
	} as const;

	const newRoute = await filterStore.toggle(toggleData);

	emit('toggle', toggleData);

	if (newRoute) router.push(newRoute);
}

function onWillDismiss() {
	isModalOpening.value = false;
}

async function openProviderSelection() {
	isModalOpening.value = true;
	await ModalHelper.openModal(
		PackagesSelectionModal,
		{ providerPlace: ProviderFilterPlace.SETTINGS, onWillDismiss },
		{ cssClass: 'packages-selection-modal' },
		ProviderFilterPlace.SETTINGS,
		true
	);
	isModalOpening.value = false;
}
</script>

<style lang="scss">
@import '~@/styles/variables';

.filter-bar-container {
	display: flex;
	align-items: center;
}

.filter-bar-seo {
	&__provider-row {
		margin-bottom: 5px;

		@media (max-width: 767px) {
			margin-left: 0 !important;

			.hidden-horizontal-scrollbar__items {
				padding-left: 0 !important;
			}
		}
	}

	&__provider-icon {
		display: inline-block;
		width: 50px;
		height: 50px;
		opacity: 0.25;
		margin: 10px 5px;
		transition: opacity 0.2s ease-in-out;
		cursor: pointer;

		&--plus > button > img {
			border-radius: 20%;
			border: 1px solid rgba(255, 255, 255, 0.7);
		}

		button {
			all: unset;
		}

		img {
			width: 100%;
		}

		&:first-child {
			margin-inline-start: 0;
		}

		&.active {
			opacity: 1;
		}

		&.disabled {
			opacity: 0.25;
			filter: grayscale(100%);
			pointer-events: none;
		}
	}
}
</style>
